import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, shippingAddressesAPI, s3API } from '../api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import HomeLocationModal from '../components/HomeLocationModal';

/**
 * Buyer-focused account settings on the web. Selling, listings, stores, payouts, and seller profile
 * tools live in the BuyDrop Seller app.
 */
const SellerSettings: React.FC = () => {
  const { user, updateUsername, updateProfilePicture, logout } = useAuth();
  const profileFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [profilePicLoadFailed, setProfilePicLoadFailed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string>('');
  const [username, setUsername] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [email, setEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState('');

  const [savedAddresses, setSavedAddresses] = useState<any[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);

  const loadSavedAddresses = useCallback(async () => {
    setLoadingAddresses(true);
    try {
      const addresses = await shippingAddressesAPI.getAll();
      const arr = Array.isArray(addresses) ? addresses : [];
      if (arr.length === 1 && !arr[0]?.is_default) {
        try {
          await shippingAddressesAPI.update(arr[0].id, { is_default: true });
        } catch (_) {}
        const refreshed = await shippingAddressesAPI.getAll();
        setSavedAddresses(Array.isArray(refreshed) ? refreshed : []);
      } else {
        setSavedAddresses(arr);
      }
    } catch (err) {
      console.error('Error loading addresses:', err);
      setSavedAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  useEffect(() => {
    loadSavedAddresses();
  }, [loadSavedAddresses]);

  useEffect(() => {
    if (location.hash === '#addresses') {
      document.getElementById('addresses')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const handleSetDefaultAddress = useCallback(
    async (id: number) => {
      try {
        await shippingAddressesAPI.update(id, { is_default: true });
        toast.success('Default address updated');
        loadSavedAddresses();
      } catch {
        toast.error('Failed to set default address');
      }
    },
    [loadSavedAddresses]
  );

  const handleDeleteAddress = useCallback(
    async (id: number) => {
      if (!window.confirm('Delete this address?')) return;
      try {
        await shippingAddressesAPI.delete(id);
        toast.success('Address removed');
        if (editingAddressId === id) setEditingAddressId(null);
        loadSavedAddresses();
      } catch {
        toast.error('Failed to delete address');
      }
    },
    [editingAddressId, loadSavedAddresses]
  );

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    setProfilePicLoadFailed(false);
  }, [user?.profile_picture]);

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Use a JPEG, PNG, GIF, or WebP image.');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller.');
      e.target.value = '';
      return;
    }
    setUploadingProfilePicture(true);
    try {
      const { uploadUrl, fileUrl } = await s3API.uploadProfileImage(file.name, file.type, 'profile');
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) {
        throw new Error('Upload failed');
      }
      await updateProfilePicture(fileUrl);
      toast.success('Profile picture updated');
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.error || err.message || 'Could not update profile picture');
    } finally {
      setUploadingProfilePicture(false);
      e.target.value = '';
    }
  };

  const handleSaveUsername = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingUsername(true);
    setUsernameError('');
    setError('');

    if (!username || username.trim().length === 0) {
      setUsernameError('Username is required');
      setSavingUsername(false);
      return;
    }
    if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters long');
      setSavingUsername(false);
      return;
    }
    if (username.length > 30) {
      setUsernameError('Username must be less than 30 characters');
      setSavingUsername(false);
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameError('Username can only contain letters, numbers, and underscores');
      setSavingUsername(false);
      return;
    }

    try {
      await updateUsername(username.trim());
      setUsernameError('');
      toast.success('Username updated successfully!');
    } catch (err: any) {
      setUsernameError(err.response?.data?.error || 'Failed to update username');
    } finally {
      setSavingUsername(false);
    }
  };

  const handleSaveEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingEmail(true);
    setEmailError('');
    setError('');

    if (!email || email.trim().length === 0) {
      setEmailError('Email is required');
      setSavingEmail(false);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      setSavingEmail(false);
      return;
    }
    if (email === user?.email) {
      setEmailError('Email is the same as current email');
      setSavingEmail(false);
      return;
    }

    try {
      await authAPI.updateEmail(email.trim());
      setEmailError('');
      toast.success('Email updated successfully! Please check your new email for verification.');
      if (user) user.email = email.trim();
    } catch (err: any) {
      setEmailError(err.response?.data?.error || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!deactivatePassword) {
      toast.error('Please enter your password to confirm');
      return;
    }
    setDeactivating(true);
    try {
      await authAPI.deactivateAccount(deactivatePassword);
      toast.success('Account deactivated successfully');
      logout();
      navigate('/login');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to deactivate account');
    } finally {
      setDeactivating(false);
      setShowDeactivateModal(false);
      setDeactivatePassword('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:pb-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Account settings</h1>
          <p className="text-gray-600">Manage your buyer account, username, and saved addresses.</p>
        </div>

        <div className="rounded-xl border border-green-200 bg-green-50 p-5 mb-8 text-left">
          <h2 className="text-sm font-semibold text-green-900 mb-1">Selling on BuyDrop</h2>
          <p className="text-sm text-green-900/90 mb-3">
            Listings, deliveries, store setup, orders, and payouts are in the <strong>BuyDrop Seller</strong> app (same
            account).
          </p>
          <Link
            to="/sell"
            className="inline-flex items-center gap-2 text-sm font-semibold text-green-800 hover:text-green-950 underline"
          >
            Get the driver app
            <i className="ri-arrow-right-line" aria-hidden />
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Profile</h2>

          <div className="mb-8 pb-8 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Profile Picture</h3>
            <div className="flex flex-row items-center gap-6">
              <div className="flex-shrink-0">
                {user?.profile_picture && !profilePicLoadFailed ? (
                  <img
                    key={user.profile_picture}
                    src={user.profile_picture}
                    alt=""
                    className="w-24 h-24 rounded-full object-cover border border-gray-200 bg-gray-50"
                    onError={() => setProfilePicLoadFailed(true)}
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center text-3xl font-semibold text-green-800 border-2 border-gray-200"
                    aria-hidden
                  >
                    {(user?.username || '?').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleProfilePictureChange}
              />
              <button
                type="button"
                onClick={() => profileFileInputRef.current?.click()}
                disabled={uploadingProfilePicture}
                className="flex-shrink-0 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 transition-colors inline-flex items-center gap-2 py-2"
              >
                {uploadingProfilePicture ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg" aria-hidden />
                    Uploading…
                  </>
                ) : (
                  'Change picture'
                )}
              </button>
            </div>
          </div>

          <form onSubmit={handleSaveUsername}>
            <label className="block text-sm font-medium text-gray-900 mb-2">Username</label>
            <div className="max-w-md flex gap-3">
              <input
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setUsernameError('');
                }}
                required
                minLength={3}
                maxLength={30}
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 text-sm"
                placeholder="Username"
              />
              <button
                type="submit"
                disabled={savingUsername || username === user?.username}
                className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded transition disabled:opacity-50"
              >
                {savingUsername ? 'Saving…' : 'Save'}
              </button>
            </div>
            {usernameError && <p className="mt-2 text-sm text-red-600">{usernameError}</p>}
            <p className="mt-2 text-xs text-gray-500">3–30 characters. Letters, numbers, and underscores.</p>
          </form>
        </div>

        <div id="addresses" className="bg-white rounded-lg border border-gray-200 p-8 mb-6 scroll-mt-24">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Shipping addresses</h2>
          <p className="text-gray-600 text-sm mb-6">Saved addresses for faster checkout.</p>

          {loadingAddresses ? (
            <div className="flex items-center gap-2 text-gray-500">
              <i className="ri-loader-4-line animate-spin" />
              <span>Loading addresses…</span>
            </div>
          ) : (
            <div className="space-y-4">
              {savedAddresses.map((addr: any) => (
                <div
                  key={addr.id}
                  className={`p-4 rounded-lg border ${addr.is_default ? 'border-green-500 bg-green-50/50' : 'border-gray-200 bg-gray-50/50'}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">
                        {addr.address ||
                          `${addr.line1 || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.postal_code || ''}`.trim()}
                      </p>
                      {(addr.apt_suite || addr.entry_code || addr.building_name) && (
                        <p className="text-sm text-gray-600 mt-1">
                          {[
                            addr.apt_suite && `Apt/Suite: ${addr.apt_suite}`,
                            addr.entry_code && `Entry code: ${addr.entry_code}`,
                            addr.building_name && `Building: ${addr.building_name}`,
                          ]
                            .filter(Boolean)
                            .join(' • ')}
                        </p>
                      )}
                      {addr.instructions && (
                        <p className="text-sm text-gray-600 mt-0.5">Instructions: {addr.instructions}</p>
                      )}
                      {addr.phone_number && (
                        <p className="text-sm text-gray-500 mt-1">Phone: {addr.phone_number}</p>
                      )}
                      {addr.is_default && (
                        <span className="inline-block mt-2 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!addr.is_default && (
                        <button
                          type="button"
                          onClick={() => handleSetDefaultAddress(addr.id)}
                          className="text-sm text-green-600 hover:text-green-700 font-medium"
                        >
                          Set default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setEditingAddressId(addr.id);
                          setShowAddressModal(true);
                        }}
                        className="text-sm text-gray-600 hover:text-gray-900"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAddress(addr.id)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setEditingAddressId(null);
                  setShowAddressModal(true);
                }}
                className="flex items-center gap-2 text-green-600 hover:text-green-700 font-medium text-sm"
              >
                <i className="ri-add-line" />
                Add new address
              </button>
              <HomeLocationModal
                isOpen={showAddressModal}
                onClose={() => {
                  setShowAddressModal(false);
                  setEditingAddressId(null);
                }}
                settingsMode
                onSaved={loadSavedAddresses}
                openForEditId={editingAddressId}
              />
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Email</h2>
          <p className="text-gray-600 text-sm mb-4">Update your email. You may need to verify the new address.</p>
          <form onSubmit={handleSaveEmail} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError('');
                }}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="your@email.com"
              />
              {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingEmail || email === user?.email}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition disabled:opacity-50 flex items-center gap-2"
              >
                {savingEmail && <i className="ri-loader-4-line animate-spin" />}
                {savingEmail ? 'Updating…' : 'Update email'}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-2 border-red-100">
          <h2 className="text-xl font-bold text-red-600 mb-4">Deactivate account</h2>
          <p className="text-gray-600 text-sm mb-4">
            This permanently disables your account. Use the driver app for seller-specific deactivation if you
            operate a store.
          </p>
          <button
            type="button"
            onClick={() => setShowDeactivateModal(true)}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition"
          >
            Deactivate account
          </button>
        </div>

        {showDeactivateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-red-600">Deactivate account</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeactivateModal(false);
                      setDeactivatePassword('');
                    }}
                    disabled={deactivating}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <i className="ri-close-line text-2xl" />
                  </button>
                </div>
                <div className="mb-6">
                  <p className="text-gray-700 mb-4">
                    This will permanently disable your account. If you use Google or Apple sign-in, follow any
                    prompts to confirm.
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your password to confirm <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={deactivatePassword}
                    onChange={(e) => setDeactivatePassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Password"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleDeactivateAccount}
                    disabled={deactivating || !deactivatePassword}
                    className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deactivating && <i className="ri-loader-4-line animate-spin" />}
                    {deactivating ? 'Deactivating…' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeactivateModal(false);
                      setDeactivatePassword('');
                    }}
                    disabled={deactivating}
                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerSettings;
